import { PrintFrame } from '@/pages/print/PrintFrame';
import { amountInWords } from '@/pages/print/amountInWords';
import { documentClass, type PaperSize } from '@/pages/print/paper';
import { formatDocumentDate } from '@core/format/dates';
import { formatMoney } from '@core/format/money';

import { useTenantBilling, type ReceiptIssuer, type SubscriptionPayment } from './api';
import { kindLabels, methodLabels } from './billing';

/** A receipt is a sheet: A5 by habit, A4 when it goes in a file. Never a roll — there is no till. */
const receiptPapers: readonly PaperSize[] = ['A5', 'A4'];

/**
 * One platform receipt, printed (docs/Platform_Billing_Design.md §7.2). Titled **Receipt**, never
 * "Invoice" — receipts only, by the owner's decision of 2026-10-18 — from HND Infotech to the shop,
 * with the period it pays for and the amount in words. A voided one reprints with VOID and the
 * reason, because the paper already handed over has to be answerable.
 */
export function ReceiptPage({
  tenantId,
  paymentId,
  onBack,
}: {
  tenantId: number;
  paymentId: number;
  onBack: () => void;
}) {
  const billing = useTenantBilling(tenantId);
  const payment = billing.data?.payments.find((p) => p.id === paymentId);

  if (!billing.data || !payment) return null;

  const company = billing.data;

  return (
    <PrintFrame
      title={`Receipt ${payment.receiptNumber}`}
      sizes={receiptPapers}
      remember="platform-receipt"
      onClose={onBack}
    >
      {(paper) => (
        <ReceiptSheet
          paper={paper}
          payment={payment}
          issuer={company.issuer}
          company={{
            name: company.tenantName,
            code: company.tenantCode,
            gstin: company.tenantGstin,
          }}
        />
      )}
    </PrintFrame>
  );
}

export function ReceiptSheet({
  paper,
  payment,
  issuer,
  company,
}: {
  paper: PaperSize;
  payment: SubscriptionPayment;
  issuer: ReceiptIssuer;
  company: { name: string; code: string; gstin: string | null };
}) {
  const free = payment.kind === 'Complimentary';
  const rupees = (value: number) => `₹${formatMoney(value)}`;

  return (
    <article id="printableCard" className={`${documentClass(paper)} relative px-7 py-6`}>
      {payment.voidedOn && (
        <p
          aria-label="Void"
          className="pointer-events-none absolute inset-0 flex items-center justify-center text-[5em] font-bold tracking-[0.2em] text-[color:var(--doc-rule)] -rotate-12"
        >
          VOID
        </p>
      )}

      <header className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-[1.35em] leading-tight font-bold">{issuer.name}</p>
          {issuer.address && <p className="text-[0.85em] whitespace-pre-line">{issuer.address}</p>}
          {issuer.phone && (
            <p className="text-[0.85em] text-[color:var(--doc-muted)]">{issuer.phone}</p>
          )}
          {issuer.email && (
            <p className="text-[0.85em] text-[color:var(--doc-muted)]">{issuer.email}</p>
          )}
          {issuer.gstin && <p className="text-[0.85em]">GSTIN {issuer.gstin}</p>}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[1.05em] font-bold tracking-[0.16em] uppercase">Receipt</p>
          <p className="font-mono text-[0.95em] font-semibold">{payment.receiptNumber}</p>
          <p className="text-[0.85em] text-[color:var(--doc-muted)]">
            {formatDocumentDate(payment.receivedOn)}
          </p>
        </div>
      </header>

      <section className="mt-5 border-t border-[color:var(--doc-rule-strong)] pt-3">
        <p className="text-[0.8em] tracking-[0.12em] text-[color:var(--doc-muted)] uppercase">
          {free ? 'Given to' : 'Received from'}
        </p>
        <p className="font-semibold">{company.name}</p>
        <p className="text-[0.85em] text-[color:var(--doc-muted)]">
          Company code <span className="font-mono">{company.code}</span>
          {company.gstin ? ` · GSTIN ${company.gstin}` : ''}
        </p>
      </section>

      <table className="mt-4 w-full border-collapse text-[0.9em]">
        <tbody>
          <Row label="For">AttireRent — {kindLabels[payment.kind].toLowerCase()}</Row>
          {payment.coversFrom && payment.coversTo && (
            <Row label="Period">
              {formatDocumentDate(payment.coversFrom)} to {formatDocumentDate(payment.coversTo)}
            </Row>
          )}
          {!free && payment.method && (
            <Row label="Paid by">
              {methodLabels[payment.method]}
              {payment.reference && <span className="font-mono"> · {payment.reference}</span>}
            </Row>
          )}
          {payment.note && <Row label="Note">{payment.note}</Row>}
        </tbody>
      </table>

      <div className="mt-4 flex items-baseline justify-between border-y border-[color:var(--doc-rule-strong)] py-2">
        <span className="font-bold">{free ? 'Amount' : 'Amount received'}</span>
        <span className="text-[1.25em] font-bold">{rupees(payment.amount)}</span>
      </div>
      {!free && (
        <p className="mt-1.5 text-[0.85em] text-[color:var(--doc-muted)]">
          {amountInWords(payment.amount)}
        </p>
      )}
      {free && (
        <p className="mt-1.5 text-[0.85em] text-[color:var(--doc-muted)]">
          These days are given free of charge.
        </p>
      )}

      {payment.voidedOn && (
        <p className="mt-4 text-[0.9em] font-semibold">
          Voided on {formatDocumentDate(payment.voidedOn)}
          {payment.voidReason ? ` — ${payment.voidReason}` : ''}. This receipt is no longer valid.
        </p>
      )}

      <footer className="mt-8 flex items-end justify-between gap-6 text-[0.8em] text-[color:var(--doc-muted)]">
        <span>Recorded by {payment.recordedBy || issuer.name}</span>
        <span className="border-t border-[color:var(--doc-rule)] pt-1">For {issuer.name}</span>
      </footer>
    </article>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr className="border-b border-[color:var(--doc-rule)]">
      <td className="w-28 py-1.5 pr-3 align-top text-[color:var(--doc-muted)]">{label}</td>
      <td className="py-1.5">{children}</td>
    </tr>
  );
}
